import { Inject, Injectable, InjectionToken, OnDestroy } from '@angular/core';
import { MsalService } from '@azure/msal-angular';
import { HubConnection, HubConnectionBuilder, HubConnectionState } from '@microsoft/signalr';
import { BehaviorSubject, firstValueFrom, Subscription } from 'rxjs';
import { environment } from '../../environments/environment';
import { ExperimentSource, Experiment } from './labpartner.service.model';
import { AppStateService } from './app-state.service';
import { ConfigurationService } from './configuration.service';
import { UserAccountService } from './user-account.service';

export interface ExperimentPresenceSnapshotDto {
  experimentId: number;
  users: ExperimentPresenceUserDto[];
}

export interface ExperimentPresenceUserDto {
  email: string;
  canWrite: boolean;
}

export type ExperimentPresenceStatus =
  | 'idle'
  | 'connecting'
  | 'connected'
  | 'reconnecting'
  | 'disconnected'
  | 'unavailable';

export type ExperimentPresenceConnectionFactory = (
  url: string,
  accessTokenFactory: () => Promise<string>,
) => HubConnection;

const PRESENCE_UPDATED_EVENT = 'experimentPresenceUpdated';
const JOIN_EXPERIMENT_METHOD = 'JoinExperiment';
const LEAVE_EXPERIMENT_METHOD = 'LeaveExperiment';

export const EXPERIMENT_PRESENCE_CONNECTION_FACTORY =
  new InjectionToken<ExperimentPresenceConnectionFactory>('EXPERIMENT_PRESENCE_CONNECTION_FACTORY', {
    providedIn: 'root',
    factory: () => (url, accessTokenFactory) => new HubConnectionBuilder()
      .withUrl(url, { accessTokenFactory })
      .withAutomaticReconnect([0, 2000, 10000, 30000])
      .build(),
  });

export const EXPERIMENT_PRESENCE_TAB_SESSION_ID = new InjectionToken<string>(
  'EXPERIMENT_PRESENCE_TAB_SESSION_ID',
  {
    providedIn: 'root',
    factory: () => createExperimentPresenceTabSessionId(),
  },
);

export const EXPERIMENT_PRESENCE_CONNECTION_ENABLED = new InjectionToken<boolean>(
  'EXPERIMENT_PRESENCE_CONNECTION_ENABLED',
  {
    providedIn: 'root',
    factory: () => !environment.testMode,
  },
);

export function createExperimentPresenceTabSessionId(
  createId: () => string = () => crypto.randomUUID(),
): string {
  const tabSessionId = createId().trim();
  if (!tabSessionId) {
    throw new Error('Experiment presence tab session ID must not be empty.');
  }

  return tabSessionId;
}

@Injectable({
  providedIn: 'root',
})
export class ExperimentPresenceService implements OnDestroy {
  private readonly subscription = new Subscription();
  private readonly usersSubject = new BehaviorSubject<readonly ExperimentPresenceUserDto[]>([]);
  private readonly statusSubject = new BehaviorSubject<ExperimentPresenceStatus>('idle');

  private connection: HubConnection | null = null;
  private joinedExperimentId: number | null = null;
  private syncSequence = 0;

  public readonly users$ = this.usersSubject.asObservable();
  public readonly status$ = this.statusSubject.asObservable();

  constructor(
    private readonly appState: AppStateService,
    private readonly accountService: UserAccountService,
    private readonly configurationService: ConfigurationService,
    private readonly msalService: MsalService,
    @Inject(EXPERIMENT_PRESENCE_CONNECTION_FACTORY)
    private readonly connectionFactory: ExperimentPresenceConnectionFactory,
    @Inject(EXPERIMENT_PRESENCE_TAB_SESSION_ID)
    private readonly tabSessionId: string,
    @Inject(EXPERIMENT_PRESENCE_CONNECTION_ENABLED)
    private readonly connectionEnabled: boolean,
  ) {
    this.subscription.add(
      this.appState.ExperimentOpened.subscribe(experiment => {
        void this.syncConnection(experiment);
      }),
    );
  }

  ngOnDestroy(): void {
    this.subscription.unsubscribe();
    void this.stopCurrentConnection();
  }

  private async syncConnection(experiment: Experiment | undefined): Promise<void> {
    const sequence = ++this.syncSequence;
    const experimentId = this.getLabPartnerExperimentId(experiment);

    if (!this.shouldConnect(experimentId)) {
      await this.stopCurrentConnection();
      return;
    }

    if (this.joinedExperimentId === experimentId
      && this.connection !== null
      && this.connection.state !== HubConnectionState.Disconnected) {
      return;
    }

    await this.stopCurrentConnection();
    if (!this.isCurrentSequence(sequence) || experimentId === null) {
      return;
    }

    const connection = this.connectionFactory(
      this.createPresenceHubUrl(),
      () => this.acquireAccessToken(),
    );
    this.connection = connection;
    this.joinedExperimentId = experimentId;
    this.statusSubject.next('connecting');

    connection.on(PRESENCE_UPDATED_EVENT, (snapshot: ExperimentPresenceSnapshotDto) => {
      if (this.connection === connection) {
        this.applySnapshot(snapshot);
      }
    });
    connection.onreconnecting(() => {
      if (this.connection === connection) {
        this.statusSubject.next('reconnecting');
      }
    });
    connection.onreconnected(() => {
      if (this.connection !== connection || this.joinedExperimentId === null) {
        return;
      }

      const reconnectedExperimentId = this.joinedExperimentId;
      void this.joinExperiment(connection, reconnectedExperimentId).catch(() => {
        if (this.connection === connection && this.joinedExperimentId === reconnectedExperimentId) {
          this.usersSubject.next([]);
          this.statusSubject.next('unavailable');
        }
      });
    });
    connection.onclose(() => {
      if (this.connection === connection) {
        this.usersSubject.next([]);
        this.statusSubject.next('disconnected');
      }
    });

    try {
      await connection.start();
      if (!this.isCurrentConnection(connection, experimentId, sequence)) {
        await this.stopConnectionSafely(connection);
        return;
      }

      await this.joinExperiment(connection, experimentId);
    } catch {
      if (this.isCurrentConnection(connection, experimentId, sequence)) {
        this.usersSubject.next([]);
        await this.stopCurrentConnection('unavailable');
      }
    }
  }

  private async joinExperiment(connection: HubConnection, experimentId: number): Promise<void> {
    const snapshot = await connection.invoke<ExperimentPresenceSnapshotDto>(
      JOIN_EXPERIMENT_METHOD,
      experimentId,
      this.tabSessionId,
    );
    if (this.connection === connection && this.joinedExperimentId === experimentId) {
      this.applySnapshot(snapshot);
      this.statusSubject.next('connected');
    }
  }

  private async stopCurrentConnection(
    nextStatus: ExperimentPresenceStatus = 'idle',
  ): Promise<void> {
    const connection = this.connection;
    const experimentId = this.joinedExperimentId;

    this.connection = null;
    this.joinedExperimentId = null;
    this.usersSubject.next([]);
    this.statusSubject.next(nextStatus);

    if (connection === null) {
      return;
    }

    connection.off(PRESENCE_UPDATED_EVENT);
    if (connection.state === HubConnectionState.Connected && experimentId !== null) {
      try {
        await connection.invoke(LEAVE_EXPERIMENT_METHOD, experimentId);
      } catch {
        // The server also removes presence when the connection stops.
      }
    }

    await this.stopConnectionSafely(connection);
  }

  private shouldConnect(experimentId: number | null): experimentId is number {
    return this.connectionEnabled && experimentId !== null && this.accountService.isLPUser();
  }

  private getLabPartnerExperimentId(experiment: Experiment | undefined): number | null {
    if (experiment?.source !== ExperimentSource.LabPartner || experiment.experimentId <= 0) {
      return null;
    }

    return experiment.experimentId;
  }

  private createPresenceHubUrl(): string {
    const baseUrl = this.configurationService.getConfig().apiServiceEndpoint.replace(/\/+$/, '');
    return `${baseUrl}/v1/experiments/presence-hub`;
  }

  private async acquireAccessToken(): Promise<string> {
    const account = this.msalService.instance.getActiveAccount()
      ?? this.msalService.instance.getAllAccounts()[0];
    if (!account) {
      throw new Error('An active account is required for experiment presence.');
    }

    const response = await firstValueFrom(this.msalService.acquireTokenSilent({
      account,
      scopes: [this.configurationService.getConfig().apiScope],
    }));
    return response.accessToken;
  }

  private applySnapshot(snapshot: ExperimentPresenceSnapshotDto): void {
    if (snapshot.experimentId !== this.joinedExperimentId) {
      return;
    }

    const usersByEmail = new Map<string, ExperimentPresenceUserDto>();
    for (const user of snapshot.users) {
      const email = user.email.trim();
      if (!email) {
        continue;
      }

      const normalizedEmail = email.toLocaleLowerCase();
      const existingUser = usersByEmail.get(normalizedEmail);
      usersByEmail.set(normalizedEmail, {
        email,
        canWrite: user.canWrite || existingUser?.canWrite === true,
      });
    }

    this.usersSubject.next([...usersByEmail.values()].sort((left, right) =>
      left.email.localeCompare(right.email)));
  }

  private isCurrentSequence(sequence: number): boolean {
    return sequence === this.syncSequence;
  }

  private isCurrentConnection(
    connection: HubConnection,
    experimentId: number,
    sequence: number,
  ): boolean {
    return this.isCurrentSequence(sequence)
      && this.connection === connection
      && this.joinedExperimentId === experimentId;
  }

  private async stopConnectionSafely(connection: HubConnection): Promise<void> {
    try {
      await connection.stop();
    } catch {
      // Presence is best-effort during navigation and shutdown.
    }
  }
}